import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth-context";

export function LoginPage() {
  const { login, signUp } = useAuth();
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isSignUp) {
        await signUp(email, password, name || undefined);
      } else {
        await login(email, password);
      }
      navigate("/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Authentication failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      {/* ── Brand side (hidden on mobile) ─────────────────────── */}
      <div className="auth-page__brand">
        <div className="auth-page__brand-inner">
          <div className="auth-brand__mark" aria-hidden="true">
            <img src="/brand/logo-full.avif" alt="" loading="eager" decoding="async" />
          </div>
        <span className="eyebrow eyebrow--white auth-brand__eyebrow">Aekam</span>
          <h2 className="display-2 text-white auth-brand__title">
            Your path to <em>inner peace</em> begins here
          </h2>
          <p className="body-lg text-white-70 auth-brand__body">
            Join thousands of seekers tracking their practice, exploring ancient wisdom,
          and walking the path to Aekam — one mindful day at a time.
          </p>
          <div className="auth-brand__list">
            {[
              { icon: "🧘", text: "Personalised daily practice tracker" },
              { icon: "📚", text: "Access to thousands of wisdom articles" },
              { icon: "🏅", text: "Earn badges for spiritual milestones" },
              { icon: "🌍", text: "Join 50,000+ seekers worldwide" },
            ].map((item) => (
              <div key={item.text} className="auth-brand__item">
                <span className="auth-brand__item-icon">{item.icon}</span>
                <span className="auth-brand__item-text">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Form side ─────────────────────────────────────────── */}
      <div className="auth-page__form-side">
        <div className="auth-card">
          <div className="auth-card__header">
            <h1 className="display-3 auth-card__title">
              {isSignUp ? "Create your account" : "Welcome back"}
            </h1>
            <p className="text-muted auth-card__subtitle">
              {isSignUp
                ? "Join the platform to access practice tools, wisdom, and your digital pass."
                : "Sign in to continue your spiritual journey."}
            </p>
          </div>

          {error && (
            <div className="alert alert--error alert--spaced-sm">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            {isSignUp && (
              <div className="form-group">
                <label className="form-label" htmlFor="name">Name</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="form-input"
                  placeholder="Your full name"
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input"
                placeholder="you@example.com"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                placeholder={isSignUp ? "Min. 8 characters" : "Your password"}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn--primary auth-form__submit"
            >
              {loading ? "Please wait…" : isSignUp ? "Create account" : "Sign in"}
            </button>
          </form>

          <p className="text-muted auth-switch-copy">
            {isSignUp ? "Already have an account?" : "Don't have an account yet?"}
            {" "}
            <button
              type="button"
              onClick={() => setIsSignUp((prev) => !prev)}
              className="auth-switch-btn"
            >
              {isSignUp ? "Sign in" : "Create free account"}
            </button>
          </p>
          {!isSignUp && (
            <p className="text-muted auth-switch-copy" style={{ marginTop: "0.5rem" }}>
              <a href="/forgot-password" className="auth-switch-btn">Forgot your password?</a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
